import React, { useRef, useState } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { UI_CONFIG } from "./constants";
import { EmptyState } from "./EmptyState";
import { ColorLegend } from "./ColorLegend";
import { MapTooltip } from "./MapTooltip";
import { useGeographicData } from "./useGeographicData";
import { useMapInitialization } from "./useMapInitialization";

interface GeographicDistributionMapProps {
  /** Optional search term to filter cities on the map */
  search?: string;
}

interface TooltipData {
  projectCount: number;
  levelLabel: string;
  x: number;
  y: number;
}

/**
 * Geographic Distribution Map Component
 * Displays project distribution across cities using an interactive Esri map
 * with color-coded circles representing project counts
 */
const GeographicDistributionMap: React.FC<GeographicDistributionMapProps> = ({
  search = "",
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const { loading, error, geoData, filteredData } = useGeographicData(search);
  const theme = useTheme();

  useMapInitialization(mapRef, filteredData, loading, setTooltipData);

  if (error) {
    return <EmptyState message={error} isError />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.customBackground.light,
          borderRadius: UI_CONFIG.borderRadius,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (geoData.length === 0) {
    return <EmptyState message="No geographic data to display" showBorder />;
  }

  if (search.trim() && filteredData.length === 0) {
    return (
      <EmptyState message={`No results found for "${search}"`} showBorder />
    );
  }

  const virtualAnchorEl = tooltipData
    ? {
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          top: tooltipData.y,
          left: tooltipData.x,
          right: tooltipData.x,
          bottom: tooltipData.y,
          x: tooltipData.x,
          y: tooltipData.y,
          toJSON: () => ({}),
        }),
      }
    : null;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        ref={mapRef}
        sx={{
          flex: 1,
          borderRadius: UI_CONFIG.borderRadius,
          overflow: "hidden",
          minHeight: 0,
        }}
      />
      <ColorLegend />
      <MapTooltip
        open={!!tooltipData}
        anchorEl={virtualAnchorEl as HTMLElement | null}
        projectCount={tooltipData?.projectCount ?? 0}
        levelLabel={tooltipData?.levelLabel ?? ""}
      />
    </Box>
  );
};

export default GeographicDistributionMap;
